import {action, computed, observable} from 'mobx';
import {ITranslate} from '../../services/LocalizationService';
import tsDate from '@techsee/techsee-date';
import {getRootStore} from '../../app.bootstrap';
import {
    IWifiAnalyzerController,
    scanTypeMapping,
    WifiAnalyzerController
} from '../../components/wifi-analyzer/controller';
import {NATIVE_PLATFORM_TYPE} from '@techsee/techsee-common/lib/constants/room.constants';
import {capitalize, get, toLower, toUpper, isEmpty} from 'lodash';
import {IWifiAnalyzerResults, ReportType} from '../../services/AngularServices/WifiAnalyzerService';
// @ts-ignore
import {
    ForwardBirthCertificateReportModalController,
    IForwardBirthCertificateReportModalController
} from './forward-birth-certificate-report-modal/controller';
// @ts-ignore
import {reportToMomentDateFormat} from '@techsee/techsee-common/lib/utils';
import {
    IPollingBirthCertificateReportModalController,
    PollingBirthCertificateReportModalController
} from './polling-birth-certificate-report-modal/controller';
import {
    IPollingBirthCertificateReportController,
    PollingBirthCertificateReportController
} from './polling-birth-certificate-report/controller';

const PAGE_SIZE = 25;

export interface IWifiAnalyzerHistoryController {
    dateFormat(date: string, format: string): string;
    selectSession(session: IWifiAnalyzerResults): void;
    formatScanTypeLabels(session: IWifiAnalyzerResults): {
        scanTypeLabel: string;
        scanTypeLabel3DHeatMap: string;
    };
    nextPage(): void;
    prevPage(): void;
    downloadBirthCertificateReportWithPolling(e: React.MouseEvent, scanId: string, reportType: ReportType): void;
    openForwardBirthCertificateReportModal(e: React.MouseEvent, scanId: string, reportType: ReportType): void;
    openForwardAggregatedReportModal(e: React.MouseEvent, roomId: string): void;
    isBirthCertificateReportEnabled(session: IWifiAnalyzerResults): boolean;
    open3dHeatMap(e: React.MouseEvent, session: IWifiAnalyzerResults): void;
    is3dHeatMapEnabled(session: IWifiAnalyzerResults): boolean;
    goToDashboard(roomId: string, scanId: string): void;
    goToInvitePage?: () => void;
    getDateFormattedByAcc: (date: string, withHours?: boolean) => string;
    searchHistory: () => Promise<void>;
    updateWifiAnalyzer: (newList: any) => void;
    wifiAnalyzerController: IWifiAnalyzerController;
    forwardBirthCertificateReportModalController: IForwardBirthCertificateReportModalController;
    pollingBirthCertificateReportModalController: IPollingBirthCertificateReportModalController;
    pollingBirthCertificateReportController: IPollingBirthCertificateReportController;
    platformNameFormatted: (deviceName: NATIVE_PLATFORM_TYPE) => string;

    readonly translate: ITranslate;
    readonly wifiAnalyzerList: IWifiAnalyzerResults[];
    readonly endDate: string;
    readonly startDate: string;
    readonly customerRef: string;
    readonly preDataCollectionCode: string;
    readonly customerNumber: string;
    readonly customerEmail: string;
    readonly selectedSession: any;
    readonly currentPage: number;
    readonly totalPages: number;
    readonly isNextAvailable: boolean;
    readonly isPrevAvailable: boolean;
    readonly getDataOnInitialLoad: boolean;
    readonly openModal: boolean;
}

type SearchCriteria = {
    startDate?: string;
    endDate?: string;
    customerRef?: string;
    roomId?: string;
    preDataCollectionCode?: string;
    scanId?: string;
    customerNumber?: string;
    customerEmail?: string;
};

export class WifiAnalyzerHistoryController implements IWifiAnalyzerHistoryController {
    private readonly _translate: ITranslate;
    private readonly _startDate: string;
    private readonly _endDate: string;
    private readonly _customerRef: string;
    private readonly _preDataCollectionCode: string;
    private readonly _scanId: string;
    private readonly _roomId: string;
    private readonly _customerNumber: string;
    private readonly _customerEmail: string;
    private readonly accountId: string;
    private readonly _wifiAnalyzerController: IWifiAnalyzerController;
    private readonly _forwardBirthCertificateReportModalController: IForwardBirthCertificateReportModalController;
    private readonly _pollingBirthCertificateReportModalController: IPollingBirthCertificateReportModalController;
    private readonly _pollingBirthCertificateReportController: IPollingBirthCertificateReportController;
    @observable private _wifiAnalyzerList: IWifiAnalyzerResults[] = [];
    @observable private _selectedSession: any = {} as any;
    @observable private _totalPages: number = 1;
    @observable private _currentPage: number = 1;
    @observable private _getDataOnInitialLoad: boolean = false;
    @observable private readonly _accDateFormat: string = '';
    @observable private readonly _timeZone: string = '';
    @observable private readonly _language: string = '';
    @observable private _openModal: boolean = false;

    readonly goToInvitePage?: () => void;
    private goToDashboardPage: (roomId: string, scanId: string) => void;

    constructor(
        translate: ITranslate,
        accountId: string,
        accDateFormat: string,
        timeZone: string,
        language: string,
        searchCriteria: SearchCriteria,
        goToDashboardPage: (roomId: string, scanId: string) => void,
        goToInvitePage?: () => void
    ) {
        this.selectSession = this.selectSession.bind(this);
        this.goToDashboardPage = goToDashboardPage;
        this.formatScanTypeLabels = this.formatScanTypeLabels.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.prevPage = this.prevPage.bind(this);
        this.getDateFormattedByAcc = this.getDateFormattedByAcc.bind(this);
        this.openForwardBirthCertificateReportModal = this.openForwardBirthCertificateReportModal.bind(this);
        this.openForwardAggregatedReportModal = this.openForwardAggregatedReportModal.bind(this);
        this.isBirthCertificateReportEnabled = this.isBirthCertificateReportEnabled.bind(this);
        this.open3dHeatMap = this.open3dHeatMap.bind(this);
        this.is3dHeatMapEnabled = this.is3dHeatMapEnabled.bind(this);
        this.goToConnectivityGuruPage = this.goToConnectivityGuruPage.bind(this);
        this._translate = translate;
        this.goToInvitePage = goToInvitePage;
        this._startDate = searchCriteria.startDate ?? '';
        this._endDate = searchCriteria.endDate ?? '';
        this._customerRef = searchCriteria.customerRef ?? '';
        this._preDataCollectionCode = searchCriteria.preDataCollectionCode ?? '';
        this._scanId = searchCriteria.scanId ?? '';
        this._roomId = searchCriteria.roomId ?? '';
        this._customerNumber = searchCriteria.customerNumber ?? '';
        this._customerEmail = searchCriteria.customerEmail ?? '';
        this.accountId = accountId;
        this._accDateFormat = accDateFormat;
        this._timeZone = timeZone;
        this._language = language.replace('_', '-');
        const rootStore = getRootStore();
        this._wifiAnalyzerController = new WifiAnalyzerController(
            rootStore.environmentService,
            rootStore.eventsLogService,
            rootStore.wifiAnalyzerService,
            rootStore.getDateFormattedByAccLocal.bind(rootStore),
            rootStore.displayTabletAsDesktop
        );
        this._forwardBirthCertificateReportModalController = new ForwardBirthCertificateReportModalController(
            translate
        );
        this._pollingBirthCertificateReportModalController = new PollingBirthCertificateReportModalController(
            translate
        );
        this._pollingBirthCertificateReportController = new PollingBirthCertificateReportController(translate);

        this.goToDashboard = this.goToDashboard.bind(this);
    }

    get translate() {
        return this._translate;
    }

    get wifiAnalyzerController() {
        return this._wifiAnalyzerController;
    }

    get forwardBirthCertificateReportModalController() {
        return this._forwardBirthCertificateReportModalController;
    }

    get pollingBirthCertificateReportModalController(): IPollingBirthCertificateReportModalController {
        return this._pollingBirthCertificateReportModalController;
    }

    get pollingBirthCertificateReportController(): IPollingBirthCertificateReportController {
        return this._pollingBirthCertificateReportController;
    }

    @computed
    get wifiAnalyzerList() {
        return this._wifiAnalyzerList;
    }

    @computed
    get startDate() {
        return this._startDate;
    }

    @computed
    get endDate() {
        return this._endDate;
    }

    @computed
    get customerRef() {
        return this._customerRef;
    }

    @computed
    get preDataCollectionCode() {
        return this._preDataCollectionCode;
    }

    @computed
    get customerNumber() {
        return this._customerNumber;
    }

    @computed
    get customerEmail() {
        return this._customerEmail;
    }

    @computed
    get selectedSession() {
        return this._selectedSession;
    }

    @computed
    get getDataOnInitialLoad() {
        return this._getDataOnInitialLoad;
    }

    @computed
    get isNextAvailable() {
        return this.currentPage < this._totalPages;
    }

    @computed
    get isPrevAvailable() {
        return this._currentPage > 1;
    }

    @computed
    get totalPages() {
        return this._totalPages;
    }

    @computed
    get currentPage() {
        return this._currentPage;
    }

    @computed
    get openModal() {
        return this._openModal;
    }

    @computed
    get roomId() {
        return this._roomId;
    }

    @computed
    get scanId() {
        return this._scanId;
    }

    @action
    selectSession(session: IWifiAnalyzerResults) {
        if (
            !isEmpty(get(session, 'channelAnalysis')) ||
            !isEmpty(get(session, 'networkDiscoveryDevices')) ||
            !isEmpty(get(session, 'networkSpeed'))
        ) {
            this._selectedSession = session;
            this._getDataOnInitialLoad = true;
            this._wifiAnalyzerController.show();
            this._wifiAnalyzerController.setResults(session);
        }
    }

    @action
    open3dHeatMap(e: React.MouseEvent, session: IWifiAnalyzerResults) {
        e.stopPropagation();
        this.goToConnectivityGuruPage(
            session._id,
            session.connectivityHeatmapData || session.connectivityGuruData,
            session.createdOn,
            session.roomId,
            session.customerId
        );
    }

    @action
    nextPage() {
        this._currentPage++;
        this.searchHistory();
    }

    @action
    prevPage() {
        this._currentPage--;
        this.searchHistory();
    }

    @action
    dateFormat(date: string, format: string) {
        return tsDate(date).format(format);
    }

    @action
    public async searchHistory(): Promise<void> {
        const pageNumber = this._currentPage;
        const startDateValue = this.startDate ? new Date(this.startDate) : undefined;
        const endDateValue = this.endDate ? new Date(this.endDate) : new Date();
        const customerRef = this.customerRef || undefined;
        const roomId = this.roomId || undefined;
        const preDataCollectionCode = this.preDataCollectionCode || undefined;
        const scanId = this.scanId || undefined;
        const customerNumber = this.customerNumber || undefined;
        const customerEmail = this.customerEmail || undefined;

        startDateValue?.setHours(0, 0, 0, 0);
        endDateValue.setHours(23, 59, 59, 999);

        if (
            startDateValue ||
            customerRef ||
            roomId ||
            preDataCollectionCode ||
            scanId ||
            customerNumber ||
            customerEmail
        ) {
            const response = await getRootStore().wifiAnalyzerService.getWifiAnalyzerHistory(
                this.accountId,
                customerRef,
                startDateValue,
                endDateValue,
                PAGE_SIZE,
                pageNumber - 1,
                preDataCollectionCode,
                scanId,
                customerNumber,
                customerEmail,
                true
            );

            this.updateWifiAnalyzer(response);
        }
    }

    @action
    public updateWifiAnalyzer(newList: any): void {
        this._totalPages = Math.ceil((newList?.totalResults || 1) / PAGE_SIZE);
        this._wifiAnalyzerList = newList.previews;
    }

    @action
    getDateFormattedByAcc(date: string, withHours: boolean = true) {
        const rawFormat = withHours ? this._accDateFormat : this._accDateFormat.split(' ')[0];
        const momentFormat = reportToMomentDateFormat(rawFormat);

        const userLocale = (navigator.language || navigator.languages?.[0] || 'en').toLowerCase().replace('_', '-');

        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || this._timeZone;

        // `date` arrives as an ISO instant, so this converts rather than
        // reinterprets — `tsDate(x).tz(z)`, not `tsDate.tz(x, z)`. `withLocale`
        // maps our locale codes onto the ones Day.js actually registers
        // (HY-639).
        return tsDate.withLocale(tsDate(date).tz(userTimeZone), userLocale).format(momentFormat);
    }

    platformNameFormatted(deviceName: NATIVE_PLATFORM_TYPE) {
        return deviceName === NATIVE_PLATFORM_TYPE.android
            ? capitalize(deviceName)
            : toLower(deviceName.charAt(0)) + toUpper(deviceName.slice(1));
    }

    isBirthCertificateReportEnabled(session: IWifiAnalyzerResults): boolean {
        return session.connectivityGuruData === undefined;
    }

    is3dHeatMapEnabled(session: IWifiAnalyzerResults): boolean {
        if (session.connectivityHeatmapData?.customerConsent?.isConsent) {
            return true;
        }

        const guruData = session.connectivityGuruData as Record<string, any>;
        if (guruData && typeof guruData === 'object') {
            if (Array.isArray(guruData.imageUrls) && guruData.imageUrls.length > 0) {
                return true;
            }

            return Object.values(guruData).some(
                (item) =>
                    item && typeof item === 'object' && typeof item.modelUrl === 'string' && item.modelUrl.length > 0
            );
        }

        return false;
    }

    formatScanTypeLabels(session: IWifiAnalyzerResults): {
        scanTypeLabel: string;
        scanTypeLabel3DHeatMap: string;
    } {
        let scanTypeLabelToReturn = '';
        let scanTypeLabel3DHeatMapToReturn = '';

        Object.entries(scanTypeMapping).forEach(([key, value]) => {
            if (!isEmpty(get(session, key))) {
                const formattedValue = value
                    .split(' ')
                    .map((word) => {
                        if (!/\d/.test(word)) {
                            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                        }

                        return word;
                    })
                    .join(' ');

                if (value === scanTypeMapping.connectivityGuruData) {
                    scanTypeLabel3DHeatMapToReturn = formattedValue;
                } else {
                    scanTypeLabelToReturn += `${formattedValue}, `;
                }
            }
        });

        return {
            scanTypeLabel: scanTypeLabelToReturn.slice(0, -2),
            scanTypeLabel3DHeatMap: scanTypeLabel3DHeatMapToReturn
        };
    }

    goToConnectivityGuruPage = (
        scanId: string,
        connectivityGuruData: object,
        createdOn: Date,
        roomId: string,
        customerId?: string
    ) => {
        getRootStore().browserUtilsService.saveToLocalStorage('connectivityGuru', {
            createdOn: this.getDateFormattedByAcc(createdOn.toString()),
            customerId,
            accountId: this.accountId,
            roomId,
            scanId
        });

        getRootStore().redirectionService.goToConnectivityGuruData();
    };

    @action
    openForwardBirthCertificateReportModal(e: React.MouseEvent, scanId: string, reportType: ReportType): void {
        e.stopPropagation();
        this._forwardBirthCertificateReportModalController.show(scanId, reportType);
    }

    @action
    openForwardAggregatedReportModal(e: React.MouseEvent, roomId: string): void {
        e.stopPropagation();
        this._forwardBirthCertificateReportModalController.showForAggregated(roomId);
    }

    @action
    downloadBirthCertificateReportWithPolling(e: React.MouseEvent, scanId: string, reportType: ReportType): void {
        e.stopPropagation();
        this._pollingBirthCertificateReportModalController.show(scanId, reportType);
        this._pollingBirthCertificateReportModalController.download();
    }

    goToDashboard(roomId: string, scanId: string): void {
        this.goToDashboardPage(roomId, scanId);
    }
}
